/**
 * NEXUMA — App tour steps
 *
 * Pure-data step definitions for the in-app guided tour, split by role.
 * The tour walks through the whole app page-by-page and explains how each
 * function is used.
 *
 * Each step:
 *   - id            stable key (also used for i18n lookup tour.steps.<id>)
 *   - target        CSS selector to spotlight, or null = centered card
 *   - placement     'right' | 'left' | 'top' | 'bottom' | 'page'
 *   - route         optional route to navigate to before showing the step
 *   - page          true → navigates to a real page, spotlights the content
 *                   area and docks the explanation card in the corner so the
 *                   user reads the instructions while seeing the actual page
 *   - skipIfMissing if the target can't be found (hidden by permissions / mobile)
 *                   silently advance instead of showing a card
 *
 * Targets reference data-tour attributes:
 *   [data-tour="sidebar"]  — the navigation sidebar (SidebarRenderer)
 *   [data-tour="content"]  — the main content area (Dashboard)
 *   [data-tour="work-clock"], [data-tour="notifications"],
 *   [data-tour="tour-help"], [data-tour="profile"] — AppBar actions
 */

export function buildTourSteps(role, t) {
  const s = (id, opts = {}) => ({
    id,
    title: t(`tour.steps.${id}.title`),
    body: t(`tour.steps.${id}.body`),
    placement: 'right',
    target: null,
    ...opts,
  });

  // A page step: navigate to `route`, spotlight the content area, dock the card.
  const p = (id, route, opts = {}) =>
    s(id, { route, target: '[data-tour="content"]', placement: 'page', page: true, ...opts });

  // Shared chrome (AppBar) + welcome/finish wrappers.
  const intro = [
    s('welcome', { target: null, route: '/' }),
    s('menu', { target: '[data-tour="sidebar"]', placement: 'right', route: '/', skipIfMissing: true }),
  ];
  const chrome = [
    s('workclock',     { target: '[data-tour="work-clock"]',    placement: 'bottom', skipIfMissing: true }),
    s('notifications', { target: '[data-tour="notifications"]', placement: 'bottom', skipIfMissing: true }),
    s('help',          { target: '[data-tour="tour-help"]',    placement: 'bottom', skipIfMissing: true }),
    s('profile',       { target: '[data-tour="profile"]',      placement: 'bottom' }),
    s('finish', { target: null, route: '/' }),
  ];

  if (role === 'worker') {
    return [
      ...intro,
      p('emp_home',         '/'),
      p('emp_profile',      '/home/reports/employee-detail'),
      p('emp_payslips',     '/my-payslips'),
      p('emp_attendance',   '/my-attendance'),
      p('emp_schedule',     '/home/reports/work-schedule-matrix'),
      p('emp_myleaves',     '/my-leaves'),
      p('emp_requestleave', '/holidays/addholiday'),
      p('emp_leavereport',  '/my-leave-report'),
      p('emp_inbox',        '/inbox'),
      p('emp_evaluation',   '/my-evaluation'),
      ...chrome,
    ];
  }

  // admin / manager — full walkthrough
  return [
    ...intro,
    p('overview',        '/'),
    p('myprofile',       '/home/reports/employee-detail'),
    p('workers',         '/workerlist'),
    p('orgchart',        '/org-chart'),
    p('team',            '/team-management'),
    p('files',           '/worker-files'),
    p('contracts',       '/contractlist'),
    p('advancements',    '/advancements'),
    p('performance',     '/performance'),
    p('myevaluation',    '/my-evaluation'),
    p('presence',        '/presence-live'),
    p('myattendance',    '/my-attendance'),
    p('schedules',       '/work-schedules'),
    p('remote',          '/remote-work'),
    p('schedulematrix',  '/home/reports/work-schedule-matrix'),
    p('fteplan',         '/fte-plan'),
    p('approvals',       '/inbox'),
    p('myleaves',        '/my-leaves'),
    p('leavebalance',    '/home/reports/leave-balance'),
    p('leavepolicies',   '/leave-policies'),
    p('leavecalendar',   '/leave-calendar'),
    p('projects',        '/projects'),
    p('jobpostings',     '/job-postings'),
    p('applications',    '/applications'),
    p('interviews',      '/interviews'),
    p('trainings',       '/trainings'),
    p('onboarding',      '/onboarding'),
    p('salaries',        '/salarieslist'),
    p('bonuses',         '/bonuses'),
    p('reimbursements',  '/reimbursements'),
    p('salary-advances', '/salary-advances'),
    p('reports',         '/home/reports'),
    p('reportattendance','/home/reports/attendance-schedule'),
    p('reportworkhours', '/home/reports/work-hours'),
    p('warnings',        '/warnings'),
    p('procedures',      '/procedures'),
    p('knowledgebase',   '/knowledge-base'),
    p('notificationscenter', '/notifications-center'),
    p('auditlog',        '/audit-log'),
    p('usermanagement',  '/user-management'),
    p('modulemanagement','/module-management'),
    p('companysettings', '/company'),
    ...chrome,
  ];
}

export default buildTourSteps;
